import { gql } from '@apollo/client';

/* -------------------------------------------------------------------------- */
/* Catalog reads                                                              */
/* -------------------------------------------------------------------------- */

export const GET_VENDOR_CATALOG = gql`
  query GetVendorCatalog($vendorId: String!) {
    getVendorCatalog(vendorId: $vendorId) {
      category
      subcategory
      addedAt
      brand
      description
      isMisfit
      misfitReason
      mrp
      name
      productType
      productId
      unit
      unitSize
      vendorCatalogId
      updatedAt
      vendorDescription
      mrp
      vendorPrice {
        amount
        currency
        formatted
      }
      vendorPrimaryImageUrl
      vendorPrimaryImageUrlThumbnail
      vendorProductName
      stockQuantity
      lowStockThreshold
      underReview
      estimatedPrepTime
      estimatedPrepTimeLabel
      minimumOrderQuantity
    }
  }
`;

/**
 * Search the vendor's catalog (used to pick a product and prefill the
 * Add-Product form). `search` is a VendorCatalogSearchInput; we only send
 * `searchQuery` + `limit`.
 */
export const MY_VENDOR_CATALOG_SEARCH = gql`
  query MyVendorCatalogSearch($vendorId: String!, $search: VendorCatalogSearchInput!) {
    myVendorCatalog(vendorId: $vendorId, search: $search) {
      success
      totalCount
      items {
        vendorCatalogId
        productId
        name
        vendorProductName
        brand
        category
        subcategory
        vendorPrimaryImageUrlThumbnail
      }
    }
  }
`;

export const MY_VENDOR_CATALOG_ITEM = gql`
  query MyVendorCatalogItem($catalogId: String!) {
    myVendorCatalogItem(catalogId: $catalogId) {
      category
      subcategory
      addedAt
      brand
      description
      isMisfit
      misfitReason
      mrp
      name
      productType
      productId
      unit
      unitSize
      vendorCatalogId
      updatedAt
      vendorDescription
      vendorImageUrls
      vendorImageUrlsThumbnail
      vendorPrice {
        amount
        currency
        formatted
      }
      vendorPrimaryImageUrlThumbnail
      vendorProductName
      vendorPrimaryImageUrl
      shortDescription
      stockQuantity
      lowStockThreshold
      estimatedPrepTime
      isGstIncluded
      isVegetarian
      isVegan
      isHalal
      isGlutenFree
      isDairyFree
      isNutFree
      isKeto
      isOrganic
      underReview
      minimumOrderQuantity
      hsnCode
    }
  }
`;

/* -------------------------------------------------------------------------- */
/* Enums used by the Add / Edit product form                                  */
/* -------------------------------------------------------------------------- */

export const GET_CATEGORIES_ENUMS = gql`
  query GetCategoriesEnums($languageCode: String) {
    getProductCategoryTypes(languageCode: $languageCode) {
      name
      value
      displayValue
    }
  }
`;

export const GET_SUBCATEGORY_TYPES = gql`
  query GetSubcategoryTypes($languageCode: String) {
    getProductSubcategoryTypes(languageCode: $languageCode) {
      categoryType
      displayValue
      imageUrl
      name
      value
    }
  }
`;

export const GET_UNIT_TYPES = gql`
  query GetUnitTypes {
    getUnitType {
      value
      displayValue
      isSupported
      enumName
    }
  }
`;

export const SUGGEST_HSN = gql`
  query SuggestHsn($sub: String!) {
    hsnCodesBySubcategory(subcategory: $sub) {
      suggestedHsnCode
      source
      primary {
        hsnCode
        cgstRate
        sgstRate
        igstRate
      }
      candidates {
        variantId
        conditionLabel
        isDefault
        hsn {
          hsnCode
          cgstRate
          sgstRate
          igstRate
        }
      }
    }
  }
`;

/* -------------------------------------------------------------------------- */
/* Catalog mutations                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Fields returned for a catalog item after add/update. Shared via a fragment so
 * the two mutations below stay in sync.
 */
const CATALOG_ITEM_FIELDS = gql`
  fragment CatalogItemFields on VendorCatalogItem {
    addedAt
    productId
    vendorId
    stockQuantity
    lowStockThreshold
    vendorDescription
    vendorProductName
    vendorPrice {
      amount
      currency
      formatted
    }
    vendorCatalogId
    isMisfit
    misfitReason
    productType
    vendorImageUrlsThumbnail
    vendorPrimaryImageUrlThumbnail
    updatedAt
    mrp
    name
    category
    description
    brand
    unit
    unitSize
    subcategory
    estimatedPrepTime
    minimumOrderQuantity
  }
`;

export const ADD_PRODUCT_TO_CATALOG = gql`
  mutation AddProductToCatalog($input: VendorCatalogAddInput!) {
    addProductToCatalog(input: $input) {
      success
      message
      errors {
        code
        field
        message
      }
      catalogItem {
        ...CatalogItemFields
      }
    }
  }
  ${CATALOG_ITEM_FIELDS}
`;

export const UPDATE_CATALOG_ITEM = gql`
  mutation UpdateCatalogItem($catalogId: String!, $input: VendorCatalogUpdateInput!) {
    updateCatalogItem(catalogId: $catalogId, input: $input) {
      success
      message
      errors {
        code
        field
        message
      }
      catalogItem {
        ...CatalogItemFields
      }
    }
  }
  ${CATALOG_ITEM_FIELDS}
`;

export const CLONE_SHOP_CATALOG = gql`
  mutation CloneShopCatalog($input: CloneShopCatalogInput!) {
    cloneShopCatalog(input: $input) {
      success
      message
      copiedCount
      skippedCount
      targetVendorId
      errors {
        code
        field
        message
      }
    }
  }
`;
